import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHomeData } from '@/hooks/useTranslatedData';

export default function LabSection() {
  const { t } = useTranslation('common');
  const labData = useHomeData().lab;
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const highlights = [labData.freezeDry, labData.microEncapsulation, labData.cleanLabel];

  const cardImages = [
    'https://readdy.ai/api/search-image?query=Abstract%20macro%20photography%20of%20delicate%20ice%20crystals%20and%20frost%20patterns%20forming%20on%20dark%20charcoal%20surface%2C%20freeze-drying%20scientific%20concept%2C%20cool%20white%20and%20silver%20tones%20against%20deep%20black%20background%2C%20elegant%20minimal%20aesthetic%2C%20high-end%20editorial%20science%20photography%2C%20soft%20bokeh&width=600&height=500&seq=40&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Abstract%20visualization%20of%20microscopic%20spheres%20and%20molecular%20particles%20floating%20in%20warm%20amber%20light%2C%20microencapsulation%20technology%20concept%2C%20golden%20bokeh%20orbs%20and%20mint%20green%20particles%2C%20scientific%20editorial%20photography%2C%20shallow%20depth%20of%20field%2C%20modern%20laboratory%20aesthetic%20on%20dark%20background&width=600&height=500&seq=41&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Minimalist%20overhead%20flat%20lay%20of%20fresh%20organic%20botanical%20leaves%20and%20herbs%20scattered%20on%20dark%20charcoal%20background%2C%20clean%20label%20natural%20concept%2C%20sage%20green%20and%20cream%20tones%2C%20soft%20natural%20lighting%2C%20elegant%20editorial%20food%20photography%2C%20wellness%20brand%20aesthetic%2C%20muted%20earthy%20colors&width=600&height=500&seq=42&orientation=landscape',
  ];

  const cardBadges = [
    { icon: 'ri-temp-cold-line', text: labData.freezeDry.badge },
    { icon: 'ri-flask-line', text: labData.microEncapsulation.badge },
    { icon: 'ri-leaf-line', text: t('misc.learnMore') },
  ];

  return (
    <section ref={sectionRef} className="bg-brand-cream py-16 md:py-24">
      <div className="w-full px-6 md:px-10 lg:px-16">
        {/* Section Header */}
        <div className="mb-12 md:mb-16">
          <span className="block text-xs font-semibold tracking-[0.2em] text-brand-gray uppercase mb-4">
            {labData.sectionLabel}
          </span>
          <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl text-brand-charcoal leading-tight">
            {labData.sectionTitle}
          </h2>
        </div>

        {/* Lab Highlights — Unified cards with background images */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {highlights.map((item, index) => (
            <div
              key={index}
              className={`group relative rounded-2xl overflow-hidden transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className="relative h-full min-h-[340px]">
                {/* Background Image */}
                <img
                  src={cardImages[index]}
                  alt={item.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                {/* Dark gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-brand-charcoal/85 via-brand-charcoal/50 to-brand-charcoal/30" />

                {/* Content */}
                <div className="relative z-10 p-6 md:p-8 h-full flex flex-col">
                  {/* Top badge */}
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-sm text-white text-xs font-medium mb-auto self-start">
                    <i className={`${cardBadges[index].icon} text-xs`}></i>
                    {cardBadges[index].text}
                  </div>

                  {/* Bottom content */}
                  <div className="mt-auto">
                    <span className="text-4xl md:text-5xl font-bold text-white mb-1 block">
                      {item.stat}
                    </span>
                    <span className="text-xs text-white/60 uppercase tracking-wider block mb-5">
                      {item.statLabel}
                    </span>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-white/70 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}